import { JsonDetails } from "@superadmin/components/JsonDetails";
import { PageShell } from "@superadmin/components/PageShell";
import { TurnBreakdown } from "@superadmin/components/TurnBreakdown";
import { formatEpoch, formatMicros, formatMs } from "@superadmin/utils/format";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@voicely/ui-mui/data-display/Table";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Box } from "@voicely/ui-mui/layout/Box";
import { Card } from "@voicely/ui-mui/layout/Card";
import { Paper } from "@voicely/ui-mui/layout/Paper";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { useUrlState } from "@voicely/web-shared/use_url_state";
import { Fragment } from "react";
import { useParams } from "react-router";
import { z } from "zod";

const SEVERITY_COLOR = { defect: "error", warning: "warning", note: "info" } as const;

const Fact = ({ label, value }: { label: string; value: string }) => (
	<Box sx={{ minWidth: 140 }}>
		<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
			{label}
		</Typography>
		<Typography variant="body2">{value}</Typography>
	</Box>
);

/**
 * The call-dump view: the same document the `call_dump` MCP tool returns,
 * with the findings first — they are the server's ranked answer to "what is
 * already known to be wrong with this call", and everything below them is the
 * evidence they were drawn from.
 */
export const SessionDetailPage = () => {
	const { sessionId = "" } = useParams();
	// The open turn's breakdown, in the URL so a link to "look at turn 7 of
	// this call" lands on turn 7 open.
	const [openTurn, setOpenTurn] = useUrlState("turn", z.string(), "");

	const dumpQuery = useQuery(orpc.superAdmin.callDump.queryOptions({ input: { id: sessionId } }));

	if (dumpQuery.isPending) {
		return (
			<PageShell title="Call" backTo="/sessions">
				<LoadingSpinner />
			</PageShell>
		);
	}

	if (dumpQuery.error) {
		return (
			<PageShell title="Call" backTo="/sessions">
				<Alert severity="error">{dumpQuery.error.message}</Alert>
			</PageShell>
		);
	}

	const dump = dumpQuery.data;

	if (!dump.found || !dump.session) {
		return (
			<PageShell title="Call" backTo="/sessions">
				<Alert severity="warning">
					No session found for <code>{sessionId}</code> — a typo, or a soft-deleted row.
				</Alert>
			</PageShell>
		);
	}

	const clocks: { label: string; value: string }[] = [
		{ label: "Grant issued", value: formatEpoch(dump.clocks.grantIssuedAt) },
		{ label: "Grant consumed", value: formatEpoch(dump.clocks.grantConsumedAt) },
		{ label: "Session started (dial)", value: formatEpoch(dump.clocks.sessionStartedAt) },
		{
			label: "Stream started (recording t=0)",
			value: formatEpoch(dump.clocks.sessionStreamStartedAt),
		},
		{ label: "Carrier answered", value: formatEpoch(dump.clocks.carrierAnsweredAt) },
		{ label: "First turn", value: formatEpoch(dump.clocks.firstTurnStartedAt) },
		{ label: "Last turn ended", value: formatEpoch(dump.clocks.lastTurnEndedAt) },
		{ label: "Session ended", value: formatEpoch(dump.clocks.sessionEndedAt) },
		{ label: "Carrier ended", value: formatEpoch(dump.clocks.carrierEndedAt) },
		{ label: "Session duration", value: formatMs(dump.clocks.sessionDurationMs) },
		{ label: "Recording duration", value: formatMs(dump.clocks.recordingDurationMs) },
	];

	return (
		<PageShell
			title={dump.session.agentName ?? "Call"}
			subtitle={dump.sessionId}
			backTo="/sessions"
			maxWidth={1000}
		>
			<Stack spacing={3}>
				{dump.findings.length > 0 && (
					<Stack spacing={1}>
						{dump.findings.map((finding) => (
							<Alert key={finding.code} severity={SEVERITY_COLOR[finding.severity]}>
								<Typography variant="body2">
									<strong>{finding.code}</strong> — {finding.message}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{Object.entries(finding.evidence)
										.map(([key, value]) => `${key}=${String(value)}`)
										.join("  ")}
								</Typography>
							</Alert>
						))}
					</Stack>
				)}

				<Card variant="outlined" sx={{ p: 2 }}>
					<Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", gap: 2 }}>
						<Fact label="Workspace" value={dump.team?.name ?? dump.team?.id ?? "—"} />
						<Fact label="Agent" value={dump.agent?.name ?? dump.session.agentName ?? "—"} />
						<Fact label="Status" value={dump.session.sessionStatus} />
						<Fact label="Started" value={formatEpoch(dump.session.startedAt)} />
						<Fact label="Duration" value={formatMs(dump.session.durationMs)} />
						<Fact label="Profile" value={dump.session.callProfile ?? "—"} />
					</Stack>
				</Card>

				<Box>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Recordings
					</Typography>
					{dump.recordings.recordings.length === 0 ? (
						<Alert severity={dump.recordings.absence ? "warning" : "info"}>
							{dump.recordings.absence
								? `No recording: ${dump.recordings.absence.reason}`
								: "No recording rows."}
						</Alert>
					) : (
						<Stack spacing={1}>
							{dump.recordings.recordings.map((recording) => (
								<Card key={recording.objectKey} variant="outlined" sx={{ p: 1.5 }}>
									<Stack
										direction="row"
										spacing={2}
										alignItems="center"
										sx={{ flexWrap: "wrap", gap: 1 }}
									>
										<Chip label={recording.channel} size="small" />
										<Typography variant="caption" color="text.secondary">
											{recording.audioFormat} · {formatMs(recording.durationMs)}
											{recording.uploaded ? "" : " · NOT UPLOADED"}
										</Typography>
										{recording.url ? (
											// Presigned and short-lived: dead ~15 min after fetch.
											// biome-ignore lint/a11y/useMediaCaption: a phone call has no caption track
											<audio controls preload="none" src={recording.url} style={{ height: 36 }} />
										) : (
											<Typography variant="caption">no playback URL</Typography>
										)}
									</Stack>
								</Card>
							))}
						</Stack>
					)}
				</Box>

				<Box>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Turns
					</Typography>
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
						Click an agent turn to see which stage ate its latency. TTFB is the silence the caller
						sat in: their speech ending to the agent's first audio byte.
					</Typography>
					{dump.turns.length === 0 ? (
						<Typography color="text.secondary" variant="body2">
							No turns — the session ended before the first utterance.
						</Typography>
					) : (
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>#</TableCell>
										<TableCell>Speaker</TableCell>
										<TableCell>Text</TableCell>
										<TableCell align="right">TTFB</TableCell>
										<TableCell align="right">Spoken</TableCell>
										<TableCell align="right">Produced</TableCell>
										<TableCell>Flags</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{dump.turns.map((turn, index) => {
										const isAgent = turn.speaker === "agent";
										const open = isAgent && openTurn === turn.id;
										// Half an agent turn's latency is stamped on the caller's
										// row before it; turns arrive ordered by `turn`.
										const previous = index > 0 ? dump.turns[index - 1] : undefined;
										const caller = previous && previous.speaker !== "agent" ? previous : null;

										return (
											<Fragment key={turn.id}>
												<TableRow
													hover={isAgent}
													onClick={() => isAgent && setOpenTurn(open ? "" : turn.id)}
													sx={{
														cursor: isAgent ? "pointer" : "default",
														"& > td": open ? { borderBottom: 0 } : {},
													}}
												>
													<TableCell>{turn.turn}</TableCell>
													<TableCell>{turn.speaker}</TableCell>
													<TableCell sx={{ maxWidth: 420 }}>
														<Typography variant="body2">{turn.text}</Typography>
														{turn.unspokenText && (
															// Generated, never heard — the visual claim must
															// match the audible truth.
															<Typography
																variant="body2"
																color="text.disabled"
																sx={{ textDecoration: "line-through" }}
															>
																{turn.unspokenText}
															</Typography>
														)}
													</TableCell>
													<TableCell align="right">{formatMs(turn.responseLatencyMs)}</TableCell>
													<TableCell align="right">{formatMs(turn.spokenMs)}</TableCell>
													<TableCell align="right">{formatMs(turn.producedMs)}</TableCell>
													<TableCell>
														<Stack direction="row" spacing={0.5}>
															{turn.interrupted && (
																<Chip label="interrupted" size="small" color="warning" />
															)}
															{turn.feedback.length > 0 && (
																<Chip
																	label={`${turn.feedback.length} feedback`}
																	size="small"
																	color="info"
																/>
															)}
														</Stack>
													</TableCell>
												</TableRow>
												{open && (
													<TableRow>
														<TableCell colSpan={7}>
															<TurnBreakdown turn={turn} caller={caller} />
														</TableCell>
													</TableRow>
												)}
											</Fragment>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Box>

				<Box>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Clocks
					</Typography>
					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableBody>
								{clocks.map((row) => (
									<TableRow key={row.label}>
										<TableCell sx={{ color: "text.secondary" }}>{row.label}</TableCell>
										<TableCell>{row.value}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>

				{dump.usage.length > 0 && (
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Metered usage
							{dump.cost && (
								<Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
									— this call cost {formatMicros(dump.cost.totalMicros, "INR")}
									{dump.cost.partial ? " (a floor: some lines have no rate)" : ""}, telephony and
									storage included, at the rates valid on its date.
								</Typography>
							)}
						</Typography>
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Kind</TableCell>
										<TableCell>Provider</TableCell>
										<TableCell>Model</TableCell>
										<TableCell>Purpose</TableCell>
										<TableCell>Source</TableCell>
										<TableCell align="right">Quantity</TableCell>
										<TableCell>Unit</TableCell>
										<TableCell align="right">Events</TableCell>
										<TableCell align="right">Cost</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{dump.usage.map((row) => (
										<TableRow key={row.id}>
											<TableCell>{row.kind}</TableCell>
											<TableCell>{row.provider}</TableCell>
											<TableCell>{row.model}</TableCell>
											<TableCell>{row.purpose}</TableCell>
											<TableCell>{row.source}</TableCell>
											<TableCell align="right">{row.quantity}</TableCell>
											<TableCell>{row.unit}</TableCell>
											<TableCell align="right">{row.events ?? "—"}</TableCell>
											<TableCell align="right">
												{row.amountMicros === null
													? "—"
													: formatMicros(Number(row.amountMicros), row.currency ?? "INR")}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				)}

				<Stack spacing={1}>
					{dump.sttEvents !== null && (
						<JsonDetails
							label={`STT events (${dump.sttEvents.length}${dump.sttEventsDropped > 0 ? `, ${dump.sttEventsDropped} dropped` : ""})`}
							value={dump.sttEvents}
						/>
					)}
					{dump.call && <JsonDetails label="Carrier call (interpreted)" value={dump.call} />}
					{dump.callRaw && <JsonDetails label="Carrier call (raw row)" value={dump.callRaw} />}
					<JsonDetails label="Session row" value={dump.session} />
					{dump.agent && <JsonDetails label="Agent (as of today)" value={dump.agent} />}
					{dump.grant && <JsonDetails label="Grant" value={dump.grant} />}
					{dump.files.length > 0 && <JsonDetails label="Files" value={dump.files} />}
				</Stack>
			</Stack>
		</PageShell>
	);
};
